/**
 * Responsibility: the small labelled readouts the card dialog is built from.
 *
 * Pulled out of the dialog when it grew tabs. They are shared by the stats
 * tab and the listings tabs, and keeping them here is what stops the dialog
 * shell turning back into one long file.
 *
 * All of them render absent data as an em dash rather than a zero. On this
 * page the difference is the whole point: "no PSA 10 has ever sold" and "a
 * PSA 10 sells for nothing" would otherwise look identical.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LaunchIcon from '@mui/icons-material/Launch';
import type { SoldPrices } from '../../api/client';
import { money, signedMoney, toNumber } from './money';

export const EM_DASH = '—';

export function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Stack spacing={0} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {hint}
        </Typography>
      ) : null}
    </Stack>
  );
}

export function Outcome({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | null;
  hint?: string;
}) {
  const amount = toNumber(value);
  return (
    <Stack spacing={0} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
        color={amount === null ? 'text.disabled' : amount > 0 ? 'success.main' : 'error.main'}
      >
        {amount === null ? EM_DASH : signedMoney(value)}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {hint}
        </Typography>
      ) : null}
    </Stack>
  );
}

/** A completed-sale figure, with how old it is. */
export function Sold({ label, sold }: { label: string; sold: SoldPrices | null | undefined }) {
  if (!sold) {
    return <Figure label={label} value={EM_DASH} hint="no sales found" />;
  }
  return (
    <Figure
      label={label}
      value={money(String(sold.average))}
      hint={`${sold.sample_size} sales · ${sold.days_since_newest}d ago${sold.stale ? ' · stale' : ''}`}
    />
  );
}

export function OutLink({ href, label, hint }: { href: string; label: string; hint: string }) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="body2"
      underline="hover"
      aria-label={hint}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minHeight: 32 }}
    >
      {label}
      <LaunchIcon sx={{ fontSize: 13 }} />
    </Link>
  );
}

/** Four figures across, the shape used throughout this dialog. */
export function Row({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
      }}
    >
      {children}
    </Box>
  );
}
